/**
 * Revenue Over Time Chart Component
 *
 * Line chart displaying revenue trends over time with granularity controls
 * (daily, weekly, monthly). Features smooth curves, gradient fill, and
 * responsive Material-UI Card layout.
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { RevenueOverTimeData } from '../../../types/financialInsights';
import {
  formatChartCurrency,
  formatChartDate,
  CHART_COLORS,
} from '../../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface RevenueOverTimeChartProps {
  data: RevenueOverTimeData | undefined;
  loading: boolean;
  granularity: 'daily' | 'weekly' | 'monthly';
  onGranularityChange: (granularity: 'daily' | 'weekly' | 'monthly') => void;
}

/**
 * Loading skeleton for chart
 */
const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: 400 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="rectangular" width={200} height={32} />
      <Skeleton variant="rectangular" width={240} height={40} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={340} />
  </Box>
);

/**
 * Revenue Over Time Chart
 */
export const RevenueOverTimeChart: React.FC<RevenueOverTimeChartProps> = ({
  data,
  loading,
  granularity,
  onGranularityChange,
}) => {
  const theme = useTheme();

  // Show skeleton during loading
  if (loading) {
    return (
      <Card>
        <CardContent>
          <ChartSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data
  if (!data || !data[granularity] || data[granularity].length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{ height: 400 }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Revenue Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different date range
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const timeSeriesData = data[granularity];
  const chartData = {
    labels: timeSeriesData.map(point =>
      formatChartDate(point.date, granularity)
    ),
    datasets: [
      {
        label: 'Revenue',
        data: timeSeriesData.map(point => point.value),
        borderColor: CHART_COLORS[0],
        backgroundColor: `${CHART_COLORS[0]}33`, // 20% opacity
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: CHART_COLORS[0],
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  // Chart options with custom tooltip
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `Revenue: ${formatChartCurrency(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
          callback: (value: any) =>
            formatChartCurrency(Number(value), 'USD', true),
        },
      },
    },
  };

  return (
    <Card>
      <CardContent>
        {/* Header with granularity controls */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Revenue Over Time
          </Typography>
          <ToggleButtonGroup
            value={granularity}
            exclusive
            onChange={(_, newGranularity) => {
              if (newGranularity !== null) {
                onGranularityChange(newGranularity);
              }
            }}
            size="small"
            aria-label="Time granularity"
          >
            <ToggleButton value="daily" aria-label="Daily">
              Daily
            </ToggleButton>
            <ToggleButton value="weekly" aria-label="Weekly">
              Weekly
            </ToggleButton>
            <ToggleButton value="monthly" aria-label="Monthly">
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 400, position: 'relative' }}>
          <Line data={chartData} options={chartOptions} />
        </Box>

        {/* Summary statistics */}
        {timeSeriesData.length > 0 && (
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Showing {timeSeriesData.length} data points
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total:{' '}
              {formatChartCurrency(
                timeSeriesData.reduce((sum, point) => sum + point.value, 0)
              )}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueOverTimeChart;
